import * as React from 'react';
import { pageview } from '../utils/ga';
import { StringKeyedDictionary } from '../typescriptHelpers';
import { AppEntity, BuildDistroBucketBase, OrganizationEntity, Release, ReleaseStepType, User } from '../types';
import { PageViewContext } from '@/types/summaryView';
import * as AnalyticEvents from '../utils/analytics';
import { trackHubspotPageView } from '../utils/hubspot';
import { getAppUsesWorkflows } from '@/helpers';

// GA & Hubspot tracking function

export function useTrackPageView(pageTitle: string, deps?: any[]) {
    document.title = pageTitle;

    const trackPageView = () => {
        pageview();
    };

    // track GA
    React.useEffect(() => {
        trackPageView();
    }, deps);

    // track hubspot
    React.useEffect(() => {
        trackHubspotPageView();
    }, deps);
}

export const allBuildDistroPropertiesFrom: (
    org?: OrganizationEntity,
    app?: AppEntity,
    bucket?: BuildDistroBucketBase,
    appsInOrg?: string[],
    appGroupsInOrg?: string[],
    context?: AnalyticEvents.Context,

    extraProperties?: StringKeyedDictionary<any>,
) => StringKeyedDictionary<any> = (org, app, bucket, appsInOrg, appGroupsInOrg, context, extraProperties) => {
    const defaultProperties = AnalyticEvents.defaultProperties();
    const usesWorkflows = !!app && getAppUsesWorkflows(app);
    const orgProperties =
        org && appGroupsInOrg && appsInOrg ?
            AnalyticEvents.orgProperties(org, appsInOrg, appGroupsInOrg, usesWorkflows)
        :   {};
    const appProperties = app ? AnalyticEvents.appProperties(app) : {};
    const bucketProperties = bucket ? AnalyticEvents.buildDistroBucketProperties(bucket) : {};
    const contextProperties = context ? AnalyticEvents.contextProperties(context) : {};

    const properties = {
        ...defaultProperties,
        ...orgProperties,
        ...appProperties,
        ...bucketProperties,
        ...contextProperties,
    };

    let allProperties = {};

    if (extraProperties) {
        allProperties = {
            ...properties,
            ...extraProperties,
        };
    } else {
        allProperties = {
            ...properties,
        };
    }
    return allProperties;
};

export const allReleasePropertiesFrom: (
    org?: OrganizationEntity,
    app?: AppEntity,
    release?: Release,
    appsInOrg?: string[],
    appGroupsInOrg?: string[],
    context?: AnalyticEvents.Context,
    releaseStepType?: ReleaseStepType,
) => StringKeyedDictionary<any> = (org, app, release, appsInOrg, appGroupsInOrg, context, releaseStepType) => {
    const defaultProperties = AnalyticEvents.defaultProperties();
    const usesWorkflows = !!app && getAppUsesWorkflows(app);
    const orgProperties =
        org && appGroupsInOrg && appsInOrg ?
            AnalyticEvents.orgProperties(org, appsInOrg, appGroupsInOrg, usesWorkflows)
        :   {};
    const appProperties = app ? AnalyticEvents.appProperties(app) : {};
    const releaseProperties = release ? AnalyticEvents.releaseProperties(release) : {};
    const contextProperties = context ? AnalyticEvents.contextProperties(context, releaseStepType) : {};

    const allProperties = {
        ...defaultProperties,
        ...orgProperties,
        ...appProperties,
        ...releaseProperties,
        ...contextProperties,
    };

    return allProperties;
};

export const summaryViewFilterPropertiesFrom: (
    org?: OrganizationEntity,
    app?: AppEntity,
    user?: User,
    appsInOrg?: string[],
    appGroupsInOrg?: string[],
    filterValues?: { destinationIds: string[]; versions: string[]; attributes: string[] },
    trackingContext?: { pageContext: PageViewContext; viewType: string },
) => StringKeyedDictionary<any> = (org, app, user, appsInOrg, appGroupsInOrg, filterValues, trackingContext) => {
    const defaultProperties = AnalyticEvents.defaultProperties();
    const usesWorkflows = !!app && getAppUsesWorkflows(app);
    const orgProperties =
        org && appGroupsInOrg && appsInOrg ?
            AnalyticEvents.orgProperties(org, appsInOrg, appGroupsInOrg, usesWorkflows)
        :   {};
    const appProperties = app ? AnalyticEvents.appProperties(app) : {};
    const userProperties = user ? AnalyticEvents.userProperties(user) : {};

    const filterProperties =
        filterValues ?
            {
                filterDestinationIds: filterValues.destinationIds,
                filterVersions: filterValues.versions,
                filterAttributes: filterValues.attributes,
            }
        :   {};

    const contextProperties =
        trackingContext ?
            {
                pageContext: trackingContext.pageContext,
                viewType: trackingContext.viewType,
            }
        :   {};

    return {
        ...defaultProperties,
        ...userProperties,
        ...orgProperties,
        ...appProperties,
        ...filterProperties,
        ...contextProperties,
    };
};
